import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useCountRelatedFields,
  useCreateRelatedFields,
  useDeleteRelation,
  useGetNotRelatedResource,
  useGetRelatedFields,
} from "@/api/related-fields";
import { ColumnFormatted, generateTable } from "@/config/dashboard/general";
import {
  tiragesColumns,
  tiragesColumnsVisibility,
} from "@/config/dashboard/tirages";
import { Tirage } from "@/lib/types";
import { ColumnDef, VisibilityState } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { RelatedRecordRowActions } from "@/components/table/RelatedRecordRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import ShowElements from "../drawer/ShowElements";
import { LinkableType } from "../LinksManagement";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";
import { GridCardWithActions } from "./GridCardWithActions";
import { TirageLinkGridCard } from "./relatedLinkGridCards";

const STORAGE_KEY = "tirage-links-table-preferences";

interface TirageLinksProps {
  id: string;
  type: LinkableType;
}

/**
 * TirageLinks component
 * @returns {JSX.Element}
 */
const TirageLinks = ({ id, type }: TirageLinksProps) => {
  const { t } = useTranslation();

  const [getPaginationRelatedTirages, setPaginationRelatedTirages] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const { data: tiragesRelated } = useGetRelatedFields(
    id,
    type,
    "relatedTirage",
    getPaginationRelatedTirages.pageSize,
    getPaginationRelatedTirages.page * getPaginationRelatedTirages.pageSize,
  );

  const { data: countRelatedTirages } = useCountRelatedFields(id, type, [
    "relatedTirage",
  ]);

  const { mutate: createRelatedFields } = useCreateRelatedFields(
    id,
    type,
    "relatedTirage",
  );
  const { mutate: deleteRelation } = useDeleteRelation(
    id,
    type,
    "relatedTirage",
  );

  const [isAddMode, setIsAddMode] = useState(false);
  const [selectedTirageId, setSelectedTirageId] = useState<string | null>(null);
  const [searchContent, setSearchContent] = useState("");

  const [getPaginationTiragesToAdd, setPaginationTiragesToAdd] =
    useState<PagingParameters>({
      page: 0,
      pageSize: 10,
    });

  const handleAddMode = () => {
    setIsAddMode(!isAddMode);
  };

  const { data: notAlreadyRelatedTirages } = useGetNotRelatedResource<Tirage>({
    resultType: "tirage",
    resourceId: id,
    pagination: getPaginationTiragesToAdd,
    searchQuery: searchContent,
  });

  const handleCreateRelation = useCallback(
    (tirageId: string) => {
      try {
        createRelatedFields([id, tirageId]);
      } catch (error) {
        console.error(error);
      }
    },
    [createRelatedFields, id],
  );

  const handleDeleteRelation = useCallback(
    (tirageId: string) => {
      deleteRelation(tirageId);
    },
    [deleteRelation],
  );

  const renderTirageRowActions = useCallback(
    (tirage: Tirage) => (
      <RelatedRecordRowActions
        isAddMode={isAddMode}
        resourceId={tirage.id}
        previewType="tirage"
        previewElements={[
          {
            label: t("tirageDescriptionNumeroEnregistrement"),
            value: tirage.tirageDescriptionNumeroEnregistrement ?? "",
            type: "text",
          },
          {
            label: t("tirageDescriptionNumeroInventaire"),
            value: tirage.tirageDescriptionNumeroInventaire ?? "",
            type: "text",
          },
          {
            label: t("tirageDescriptionNotes"),
            value: tirage.tirageDescriptionNotes ?? "",
            type: "richtext",
          },
          {
            label: t("attachments"),
            value: tirage.attachments ?? [],
            type: "image",
          },
        ]}
        onDelete={() => handleDeleteRelation(tirage.id)}
        onAdd={() => handleCreateRelation(tirage.id)}
      />
    ),
    [handleCreateRelation, handleDeleteRelation, isAddMode, t],
  );

  const columns: ColumnDef<Tirage, any>[] = useMemo(() => {
    return [
      ...generateTable(tiragesColumns, "tirage"),
      createActionsColumnDef<Tirage>(({ row }) =>
        renderTirageRowActions(row.original),
      ),
    ];
  }, [renderTirageRowActions]);

  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => {
      const saved = readTablePreferences(STORAGE_KEY);
      if (saved) {
        const { visibility } = saved;
        return (
          visibility || {
            ...tiragesColumnsVisibility,
            vignette: true,
            tiragePublicationStatus: false,
          }
        );
      }
      return {
        ...tiragesColumnsVisibility,
        vignette: true,
        tiragePublicationStatus: false,
      };
    },
  );

  const flatVisibleTirageColumns = tiragesColumns.flatMap(
    (column: ColumnFormatted) => (column.visible ? column.key : []),
  );

  const [columnsOrder, setColumnsOrder] = useState<string[]>(() => {
    const saved = readTablePreferences(STORAGE_KEY);
    if (saved) {
      const { order } = saved;
      return order || flatVisibleTirageColumns;
    }
    return flatVisibleTirageColumns;
  });

  const tableOptions = {
    enableColumnResizing: true,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnsOrder,
    state: {
      columnVisibility: columnVisibility,
      columnOrder: columnsOrder,
    },
  };

  const relatedPaginationProps = {
    pagination: getPaginationRelatedTirages,
    setPage: setPaginationRelatedTirages,
    totalSize: countRelatedTirages?.relatedTirage ?? 0,
  };

  return (
    <RelatedLinksListGridLayout
      storageKey={STORAGE_KEY}
      title={t("linkedTirages")}
      isAddMode={isAddMode}
      onBack={handleAddMode}
      onToggleAdd={handleAddMode}
      searchInput={
        <div className="flex items-center gap-2">
          <Input
            placeholder={t("search")}
            className="w-80"
            value={searchContent}
            onChange={(e) => setSearchContent(e.target.value)}
          />
        </div>
      }
      addModeTable={
        <DataTable
          columns={columns}
          data={notAlreadyRelatedTirages?.data ?? []}
          pagination={{
            pagination: getPaginationTiragesToAdd,
            setPage: setPaginationTiragesToAdd,
            totalSize: notAlreadyRelatedTirages?.totalSize ?? 0,
          }}
          storageKey={STORAGE_KEY}
          options={tableOptions}
        />
      }
      listTable={
        <DataTable
          columns={columns}
          data={tiragesRelated ?? []}
          pagination={relatedPaginationProps}
          storageKey={STORAGE_KEY}
          options={tableOptions}
        />
      }
      gridContent={
        <>
          {tiragesRelated?.map((tirage: Tirage) => (
            <div key={tirage.id}>
              <GridCardWithActions actions={renderTirageRowActions(tirage)}>
                <TirageLinkGridCard
                  tirage={tirage}
                  t={t}
                  onTextClick={() => setSelectedTirageId(tirage.id)}
                />
              </GridCardWithActions>
              <ShowElements
                type="tirage"
                open={selectedTirageId === tirage.id}
                onOpenChange={(open) => {
                  if (!open) {
                    setSelectedTirageId(null);
                  }
                }}
                trigger={<span />}
                elements={[
                  {
                    label: t("tirageDescriptionNumeroEnregistrement"),
                    value: tirage.tirageDescriptionNumeroEnregistrement ?? "",
                    type: "text",
                  },
                  {
                    label: t("tirageDescriptionNotes"),
                    value: tirage.tirageDescriptionNotes ?? "",
                    type: "richtext",
                  },
                  {
                    label: t("attachments"),
                    value: tirage.attachments ?? [],
                    type: "image",
                  },
                ]}
              />
            </div>
          ))}
        </>
      }
      relatedPagination={relatedPaginationProps}
    />
  );
};

export default TirageLinks;
